/**
 * Footer Component - HI-SI Landing Page
 * HISIP-016: Legal links and brand credits
 */

import { motion } from 'framer-motion';
import { Brain, ExternalLink } from 'lucide-react';

interface FooterProps {
  onTerms: () => void;
  onPrivacy: () => void;
  onContact: () => void;
}

const FOOTER_LINKS = [
  {
    title: 'Product',
    links: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
      { name: 'Demo', href: '#demo' },
      { name: 'API', href: '/docs/api', external: true }
    ]
  },
  {
    title: 'Company',
    links: [
      { name: 'About', href: '/about' },
      { name: 'Blog', href: '/blog' },
      { name: 'Careers', href: '/careers' },
      { name: 'Contact', href: '/contact' }
    ]
  },
  {
    title: 'Support',
    links: [
      { name: 'Help Center', href: '/help' },
      { name: 'Community', href: '/community' },
      { name: 'Status', href: 'https://status.hi-si.ai', external: true },
      { name: 'Feedback', href: '/feedback' }
    ]
  }
];

export default function Footer({ onTerms, onPrivacy, onContact }: FooterProps) {
  return (
    <footer className="relative border-t border-white/10">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/95 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {/* Logo */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-light">
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      HI-SI
                    </span>
                  </div>
                  <div className="text-xs text-white/50 font-mono uppercase tracking-wider">
                    High Signal Intelligence
                  </div>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Transform your ideas into professional videos through our seven-stage AI pipeline. 
                From script to screen in minutes, not hours.
              </p>
              
              {/* Social Links - Placeholder */}
              <div className="flex items-center gap-4">
                {[
                  { name: 'Twitter', href: 'https://twitter.com/hisi_ai' },
                  { name: 'LinkedIn', href: 'https://linkedin.com/company/hisi-ai' },
                  { name: 'YouTube', href: 'https://youtube.com/@hisi-ai' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-white/60 group-hover:text-white/80" />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
          
          {/* Footer Links */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {FOOTER_LINKS.map((section, sectionIndex) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-white font-medium mb-4">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <motion.li
                        key={link.name}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: sectionIndex * 0.1 + linkIndex * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <a
                          href={link.href}
                          target={link.external ? '_blank' : undefined}
                          rel={link.external ? 'noopener noreferrer' : undefined}
                          className="text-white/70 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                        >
                          {link.name}
                          {link.external && (
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
                          )}
                        </a>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="pt-8 border-t border-white/10"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <div className="text-white/50 text-sm">
              © 2024 HI-SI. All rights reserved. Powered by High Signal Intelligence.
            </div>
            
            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={onTerms}
                className="text-white/70 hover:text-white transition-colors"
              >
                Terms of Service
              </button>
              <button
                onClick={onPrivacy}
                className="text-white/70 hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={onContact}
                className="text-white/70 hover:text-white transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </motion.div>
        
        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-6 px-6 py-3 rounded-full bg-black/30 backdrop-blur-xl border border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-white/60 text-xs">Enterprise Grade Security</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full" />
              <span className="text-white/60 text-xs">SOC2 Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full" />
              <span className="text-white/60 text-xs">GDPR Ready</span>
            </div>
          </div>
        </motion.div>
        
      </div>
    </footer>
  );
}